import { inject, Injectable } from '@angular/core';
import { NgxIndexedDBService } from 'ngx-indexed-db';
import { Observable, from, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class IndexedDbService {
  dbService = inject(NgxIndexedDBService);
  
  // Agregar datos
  addData(storeName: string, data: any): Observable<any> {
    return from(this.dbService.add(storeName, data));
  }

  // Obtener un registro por ID
  getByID(storeName: string, id: string): Observable<any> {
    return from(this.dbService.getByID<any>(storeName, id));
  }

  // Modificar datos
  updateData(storeName: string, id: string, updatedData: any): Observable<any> {
    return new Observable(observer => {
      // Obtener el registro existente
      this.dbService.getByID(storeName, id).subscribe({
        next: (data: any) => {
          if (data) {
            // Si se encuentra, actualizar el registro
            const updatedRecord = { ...data, ...updatedData };
            this.dbService.update(storeName, updatedRecord).subscribe({
              next: () => {
                observer.next(updatedRecord); // Emitir el registro actualizado
                observer.complete(); // Completar el observable
              },
              error: error => observer.error(error), // Manejo de errores en la actualización
            });
          } else {
            observer.error('Data not found'); // Manejo de error si no se encuentra el registro
          }
        },
        error: error => observer.error(error), // Manejo de errores en la obtención del registro
      });
    });
  }

  // Leer todos los datos
  getAllData(storeName: string): Observable<any[]> {
    if (!this.dbService?.getAll) {
      return of([]);
    }
    return new Observable(observer => {
      this.dbService.getAll(storeName).subscribe({
        next: data => {
          // Si el store existe, devolver los datos
          observer.next(data);
          observer.complete();
        },
        error: () => {
          // Si ocurre un error, asumimos que el store no existe
          const storeSchema = {
            store: storeName,
            storeConfig: { keyPath: 'pointId', autoIncrement: false }, // Cambiado a pointId
            storeSchema: [
              { name: 'pointId', keypath: 'pointId', options: { unique: true } },
              { name: 'data', keypath: 'data', options: { unique: false } },
            ],
          };
          // Crear el store
          try {
            this.dbService.createObjectStore(storeSchema);
            // Retornar un array vacío tras la creación
            observer.next([]);
            observer.complete();
          } catch (error) {
            observer.error(`Error al crear el store: ${error}`);
          }
        },
      });
    });
  }

  // Eliminar todos los datos en un store
  clearStore(storeName: string): Observable<boolean> {
    return new Observable(observer => {
      this.dbService.clear(storeName).subscribe({
        next: () => {
          observer.next(true);
          observer.complete();
        },
        error: () => {
          observer.next(true);
          observer.complete();
        },
      });
    });
  }

  // Eliminar un registro por ID
  deleteData(storeName: string, id: string): Observable<any> {
    return from(this.dbService.delete(storeName, id));
  }
}
